import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Empty } from "@/components/Shared";
import { ProductThumb } from "@/components/QuoteParts";
import { useTableColumns, ColumnChooser } from "@/components/ColumnChooser";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Boxes, Package, AlertTriangle, PackageX, Warehouse, Search, Plus, SlidersHorizontal, History, MapPin, Truck, Building2, ChevronRight } from "lucide-react";
import Purchasing from "@/components/inventory/Purchasing";
import Suppliers from "@/components/inventory/Suppliers";
import BinsDialog from "@/components/inventory/BinsDialog";

const TABS = [
  { key: "overview", label: "Overview", icon: Boxes },
  { key: "stock", label: "Stock", icon: Package },
  { key: "warehouses", label: "Warehouses", icon: Warehouse },
  { key: "purchasing", label: "Purchasing", icon: Truck },
  { key: "suppliers", label: "Suppliers", icon: Building2 },
  { key: "settings", label: "Settings", icon: SlidersHorizontal, owner: true },
];
const STATUS = { ok: "text-[#0B7C5A]", low: "text-[#B45309]", out: "text-[#B91C1C]" };
const STATUS_BG = { ok: "bg-[#DCFCE7] text-[#15803D]", low: "bg-[#FEF3C7] text-[#B45309]", out: "bg-[#FEE2E2] text-[#B91C1C]" };

export default function Inventory() {
  const { user } = useAuth();
  const isOwner = user?.is_super_admin || ["Owner", "Manager"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (k) => setParams(k === "overview" ? {} : { tab: k });
  const [warehouses, setWarehouses] = useState([]);
  const [wh, setWh] = useState("");

  useEffect(() => {
    api.get("/warehouses").then((r) => { setWarehouses(r.data); setWh(r.data.find((w) => w.is_default)?.id || r.data[0]?.id || ""); }).catch(() => {});
  }, []);

  const tabs = TABS.filter((t) => !t.owner || isOwner);
  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Multi-warehouse stock, live availability and movements"
        actions={warehouses.length > 1 && tab !== "warehouses" && tab !== "settings" ? (
          <Select value={wh} onValueChange={setWh}>
            <SelectTrigger className="h-10 w-full sm:w-48 bg-white" data-testid="inv-warehouse-select"><SelectValue /></SelectTrigger>
            <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
          </Select>) : null} />

      <div className="md:hidden flex gap-2 overflow-x-auto pb-2 mb-4 border-b border-[#E6E8F0]">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`inv-tab-${t.key}`}
            className={`shrink-0 flex items-center gap-2 px-3 h-10 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? "border-[#101D51] text-[#101D51]" : "border-transparent text-[#6B7189]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>))}
      </div>

      {tab === "overview" && <Overview wh={wh} />}
      {tab === "stock" && <Stock wh={wh} />}
      {tab === "warehouses" && <Warehouses rows={warehouses} setRows={setWarehouses} isOwner={isOwner} />}
      {tab === "purchasing" && <Purchasing warehouses={warehouses} isOwner={isOwner} />}
      {tab === "suppliers" && <Suppliers isOwner={isOwner} />}
      {tab === "settings" && isOwner && <SettingsTab />}
    </div>);
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-[#6B7189] mb-1"><Icon className="w-4 h-4" style={{ color }} /> {label}</div>
      <div className="text-2xl font-bold text-[#101D51]">{value}</div>
    </Card>);
}

function Overview({ wh }) {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/inventory/dashboard?warehouse_id=${wh}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [wh]);
  if (!d) return <Loading />;
  return (
    <div className="space-y-5" data-testid="inv-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Package} label="Tracked products" value={d.total_products} color="#101D51" />
        <Stat icon={Boxes} label="Total units" value={d.total_units} color="#0B7C5A" />
        <Stat icon={AlertTriangle} label="Low stock" value={d.low_stock_count} color="#B45309" />
        <Stat icon={PackageX} label="Out of stock" value={d.out_of_stock_count} color="#B91C1C" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Boxes} label="Reserved units" value={d.reserved_units} color="#6D28D9" />
        <Stat icon={Package} label="Stock value" value={formatINR(d.stock_value_paise)} color="#101D51" />
      </div>

      {d.low_stock_items.length > 0 && (
        <Card className="p-4">
          <div className="font-semibold text-[#101D51] mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#B45309]" /> Reorder soon</div>
          <div className="space-y-1.5">{d.low_stock_items.map((i) => (
            <div key={i.product_id} className="flex items-center justify-between text-sm">
              <span className="text-[#101D51] truncate">{i.name} <span className="text-[#6B7189]">· {i.code}</span></span>
              <span className="text-[#B45309] font-semibold shrink-0">{i.available} / reorder {i.reorder_level}</span>
            </div>))}</div>
        </Card>)}

      <Card className="p-4">
        <div className="font-semibold text-[#101D51] mb-2 flex items-center gap-2"><History className="w-4 h-4" /> Recent movements</div>
        {d.recent_movements.length === 0 ? <div className="text-sm text-[#6B7189] py-4">No stock movements yet.</div> : (
          <div className="divide-y divide-[#F0F1F5]">{d.recent_movements.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2 text-sm">
              <div className="min-w-0"><div className="text-[#101D51] truncate">{m.product?.name || m.product_id}</div><div className="text-[11px] text-[#6B7189]">{m.movement_type} · {new Date(m.created_at).toLocaleString()}</div></div>
              <span className={`font-semibold shrink-0 ${m.quantity >= 0 ? "text-[#0B7C5A]" : "text-[#B91C1C]"}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity}</span>
            </div>))}</div>)}
      </Card>
    </div>);
}

const STOCK_COLUMNS = [
  { key: "code", label: "Code", render: (p) => <span className="font-mono text-xs">{p.code}</span> },
  { key: "name", label: "Name", render: (p) => <span className="font-medium text-[#101D51]">{p.name}</span> },
  { key: "brand", label: "Brand", render: (p) => <span className="text-[#6B7189]">{p.brand || "—"}</span> },
  { key: "unit", label: "Unit", render: (p) => <span className="text-[#6B7189]">{p.unit}</span> },
  { key: "onhand", label: "On hand", right: true, render: (p) => <span className="text-[#101D51]">{p.tracked ? p.available + (p.reserved || 0) : "—"}</span> },
  { key: "available", label: "Available", right: true, render: (p) => <span className={`font-semibold ${STATUS[p.status]}`} data-testid={`inv-avail-${p.code}`}>{p.tracked ? p.available : "—"}</span> },
  { key: "reserved", label: "Reserved", right: true, render: (p) => <span className="text-[#6B7189]">{p.reserved || 0}</span> },
  { key: "status", label: "Status", center: true, render: (p) => <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_BG[p.status] || "bg-[#F5F6FA] text-[#6B7189]"}`}>{!p.tracked ? "Not tracked" : p.status === "ok" ? "In stock" : p.status === "low" ? "Low" : "Out"}</span> },
];

function Stock({ wh }) {
  const navigate = useNavigate();
  const colCtl = useTableColumns("td_inv_stock_cols", STOCK_COLUMNS);
  const cols = colCtl.cols;
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    setData(null);
    api.get(`/inventory/stock?warehouse_id=${wh}&q=${encodeURIComponent(q)}&filter=${filter}&page_size=200`)
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [wh, q, filter]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const reorder = (e, p) => { e.stopPropagation(); navigate(`/inventory?tab=purchasing&reorder=${p.id}`); };
  const isLow = (p) => p.tracked && (p.status === "low" || p.status === "out");

  return (
    <div data-testid="inv-stock">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
          <Input data-testid="inv-stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product name, code or brand…" className="pl-9 h-10 bg-white" /></div>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-10 w-full sm:w-44 bg-white" data-testid="inv-stock-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All products</SelectItem><SelectItem value="tracked">Tracked only</SelectItem><SelectItem value="low">Low stock</SelectItem><SelectItem value="out">Out of stock</SelectItem></SelectContent>
        </Select>
        <div className="hidden sm:block"><ColumnChooser ctl={colCtl} testid="inv-columns" /></div>
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <Empty title="No products" hint="Try a different search or filter." /> : (
        <>
          <Card className="hidden sm:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
                <th className="px-4 py-3 font-medium w-14"></th>
                {cols.map((c) => <th key={c.key} className={`px-4 py-3 font-medium ${c.right ? "text-right" : c.center ? "text-center" : ""}`}>{c.label}</th>)}
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>{data.items.map((p) => (
                <tr key={p.id} className="border-t border-[#E6E8F0] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/products/${p.id}`)} data-testid={`inv-row-${p.code}`}>
                  <td className="px-4 py-2"><ProductThumb url={p.image_url} size={9} /></td>
                  {cols.map((c) => <td key={c.key} className={`px-4 py-3 ${c.right ? "text-right" : c.center ? "text-center" : ""}`}>{c.render(p)}</td>)}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isLow(p) && <button onClick={(e) => reorder(e, p)} className="text-xs font-medium text-[#0B7C5A] border border-[#0B7C5A]/40 rounded-lg px-2 py-1 mr-2 hover:bg-[#ECFDF5]" data-testid={`inv-reorder-${p.code}`}>Reorder</button>}
                    <ChevronRight className="w-4 h-4 text-[#6B7189] inline" />
                  </td>
                </tr>))}</tbody>
            </table>
          </Card>
          <div className="sm:hidden space-y-2">{data.items.map((p) => (
            <Card key={p.id} className="p-3 active:bg-[#F5F6FA] flex items-center gap-3" onClick={() => navigate(`/products/${p.id}`)} data-testid={`inv-card-${p.code}`}>
              <ProductThumb url={p.image_url} size={10} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#101D51] truncate leading-tight">{p.name}</div>
                <div className="text-[11px] text-[#6B7189] truncate">{p.brand ? `${p.brand} · ` : ""}{p.code}</div>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-sm font-semibold ${STATUS[p.status]}`}>{p.tracked ? p.available : "—"}<span className="text-[10px] text-[#6B7189] font-normal"> {p.unit}</span></div>
                {isLow(p)
                  ? <button onClick={(e) => reorder(e, p)} className="mt-1 text-[11px] font-medium text-[#0B7C5A]" data-testid={`inv-reorder-${p.code}`}>Reorder</button>
                  : <div className="text-[10px] text-[#6B7189]">{p.reserved > 0 ? `${p.reserved} reserved` : (p.tracked ? "available" : "not tracked")}</div>}
              </div>
            </Card>))}</div>
        </>)}
    </div>);
}

function Warehouses({ rows, setRows, isOwner }) {
  const navigate = useNavigate();
  const [edit, setEdit] = useState(null);
  const [bins, setBins] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", address: "", is_default: false });
  const [saving, setSaving] = useState(false);
  const open = (w) => { setEdit(w || {}); setForm(w ? { name: w.name, code: w.code || "", address: w.address || "", is_default: w.is_default } : { name: "", code: "", address: "", is_default: rows.length === 0 }); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Warehouse name is required");
    setSaving(true);
    try {
      const { data } = edit?.id ? await api.put(`/warehouses/${edit.id}`, form) : await api.post("/warehouses", form);
      const r = await api.get("/warehouses"); setRows(r.data); setEdit(null); toast.success(edit?.id ? "Warehouse updated" : "Warehouse added");
      void data;
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="inv-warehouses">
      {isOwner && <div className="mb-3"><Button onClick={() => open(null)} className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="inv-add-warehouse"><Plus className="w-4 h-4" /> Add warehouse</Button></div>}
      <Card className="hidden sm:block overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
            <th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Code</th>
            <th className="px-4 py-3 font-medium">Address</th><th className="px-4 py-3 font-medium text-center">Default</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>{rows.map((w) => (
            <tr key={w.id} className="border-t border-[#E6E8F0] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/inventory/warehouses/${w.id}`)} data-testid={`inv-wh-${w.id}`}>
              <td className="px-4 py-3 font-medium text-[#1D4ED8]">{w.name}</td>
              <td className="px-4 py-3 text-[#6B7189] font-mono text-xs">{w.code || "—"}</td>
              <td className="px-4 py-3 text-[#6B7189]">{w.address || "—"}</td>
              <td className="px-4 py-3 text-center">{w.is_default ? <span className="text-[10px] bg-[#EAF3FF] text-[#1D4ED8] rounded-full px-2 py-0.5">Default</span> : "—"}</td>
              <td className="px-4 py-3 text-right"><div className="flex gap-1 justify-end">
                <button onClick={(e) => { e.stopPropagation(); setBins(w); }} className="h-8 px-2 rounded-lg border border-[#E6E8F0] text-[#6B7189] text-xs flex items-center gap-1" data-testid={`inv-wh-bins-${w.id}`}><MapPin className="w-3.5 h-3.5" /> Bins</button>
                {isOwner && <button onClick={(e) => { e.stopPropagation(); open(w); }} className="h-8 px-2 rounded-lg border border-[#E6E8F0] text-[#6B7189] text-xs">Edit</button>}
              </div></td>
            </tr>))}</tbody>
        </table>
      </Card>
      <div className="sm:hidden space-y-2">{rows.map((w) => (
        <Card key={w.id} className="p-3 flex items-center justify-between cursor-pointer" onClick={() => navigate(`/inventory/warehouses/${w.id}`)} data-testid={`inv-wh-card-${w.id}`}>
          <div><div className="font-medium text-[#101D51] flex items-center gap-2">{w.name}{w.is_default && <span className="text-[10px] bg-[#EAF3FF] text-[#1D4ED8] rounded-full px-2 py-0.5">Default</span>}</div><div className="text-[11px] text-[#6B7189]">{w.code}{w.address ? ` · ${w.address}` : ""}</div></div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-9" onClick={(e) => { e.stopPropagation(); setBins(w); }}><MapPin className="w-4 h-4" /></Button>
            {isOwner && <Button variant="outline" className="h-9" onClick={(e) => { e.stopPropagation(); open(w); }}>Edit</Button>}
          </div>
        </Card>))}</div>
      {bins && <BinsDialog warehouse={bins} onClose={() => setBins(null)} />}
      {edit && (
        <Dialog open onOpenChange={() => setEdit(null)}><DialogContent className="sm:max-w-sm" data-testid="inv-wh-dialog">
          <DialogHeader><DialogTitle>{edit.id ? "Edit warehouse" : "Add warehouse"}</DialogTitle><DialogDescription>Stock is tracked per warehouse.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="inv-wh-name" /></div>
            <div className="grid grid-cols-2 gap-2"><div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1" /></div>
              <div className="flex items-end gap-2 pb-1"><Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="inv-wh-default" /><span className="text-sm text-[#6B7189]">Default</span></div></div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="inv-wh-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent></Dialog>)}
    </div>);
}

function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/inventory/config").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const patch = async (upd) => {
    setSaving(true);
    try { const { data } = await api.put("/inventory/config", upd); setCfg(data); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  if (!cfg) return <Loading />;
  const Row = ({ title, desc, k }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#F0F1F5]">
      <div className="pr-4"><div className="text-sm font-medium text-[#101D51]">{title}</div><div className="text-xs text-[#6B7189]">{desc}</div></div>
      <Switch checked={!!cfg[k]} disabled={saving} onCheckedChange={(v) => patch({ [k]: v })} data-testid={`inv-cfg-${k}`} />
    </div>);
  return (
    <Card className="p-4 max-w-xl" data-testid="inv-settings">
      <Row k="track_inventory" title="Track inventory" desc="Turn on stock tracking, availability on quotes and automatic order deduction." />
      <Row k="allow_negative_inventory" title="Allow negative inventory" desc="Permit orders even when stock is insufficient (backorders)." />
      <div className="flex items-center justify-between py-3">
        <div className="pr-4"><div className="text-sm font-medium text-[#101D51]">Order stock policy</div><div className="text-xs text-[#6B7189]">Deduct removes stock on order; Reserve holds it until fulfilled.</div></div>
        <Select value={cfg.order_policy} onValueChange={(v) => patch({ order_policy: v })}>
          <SelectTrigger className="h-9 w-32" data-testid="inv-cfg-policy"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="deduct">Deduct</SelectItem><SelectItem value="reserve">Reserve</SelectItem></SelectContent>
        </Select>
      </div>
    </Card>);
}
